<template>
  <div class="message-card" :class="{ 'message-card--open': currentMessageViewOption === 0, 'message-card--error': showValidationError }">
    <div class="message-card__header" role="button" tabindex="0" :aria-expanded="currentMessageViewOption === 0 ? 'true' : 'false'"
      @click="toggleView" @keydown.enter.self.prevent="toggleView" @keydown.space.self.prevent="toggleView">
      <i class="fa fa-bars message-card__grip" title="Drag to reorder" aria-hidden="true"></i>
      <i class="fa fa-chevron-down message-card__chevron" aria-hidden="true"></i>
      <span class="message-card__title">{{'Message '+(index+1)}}</span>
      <span v-if="showValidationError" class="message-card__badge"><i class="fa fa-exclamation-circle" aria-hidden="true"></i> Needs a fix</span>
      <!-- .stop keeps these clicks from opening or closing the message -->
      <div class="message-card__actions" @click.stop>
        <button type="button" class="message-action message-action--primary" @click="translate()" title="Translate the Global English text into the switched-on languages">
          <i class="fa fa-language" aria-hidden="true"></i> Translate
        </button>
        <button type="button" class="message-action" @click="lockAll()" title="Switch off translation for every language">
          <i class="fa fa-lock" aria-hidden="true"></i> Lock All
        </button>
        <button type="button" class="message-action" @click="unlockAll()" title="Switch on translation for every language">
          <i class="fa fa-unlock" aria-hidden="true"></i> Unlock All
        </button>
        <button type="button" class="message-action message-action--icon message-action--danger" @click="deleteMessage(index)" title="Delete message">
          <i class="fa fa-trash" aria-hidden="true"></i>
        </button>
      </div>
    </div>
    <div :id="'content-message-'+index" :class="[messageViewClass, messageStyleClass]">
      <div v-for="(text, country) in displayMessage" :key="country" class="message-country-group">
        <table class='fixed-table'  v-if="Object.keys(text).length">
          <tr :key="language" v-for="(messageRow, language, iSub) in text">
            <td class='message-country' :rowspan="Object.keys(text).length" v-if="iSub === 0"><span class="message-country__name">{{getCountryName(country)}}</span></td>
            <td class='message-language'>
              <span class="message-language__code">{{language}}</span>
              <label v-if="getCountryName(country) !== 'Global' && translateSwitch[country]" class="switch" title="Include this language when translating">
                <input type="checkbox" v-model="translateSwitch[country][language]" @change="emitUpdatedMessage = true">
                <span class="slider round"></span>
              </label>
            </td>
            <td class='clear-translation'><button type="button" class="message-erase" @click="eraseTranslation(country, language)" title="Erase this translation"><i class="fa fa-times-circle" aria-hidden="true"></i></button></td>
            <td><textarea-autosize v-model="displayMessage[country][language]" :value="messageRow" class="form-control message-text" style="min-height: 50px; rows=1" placeholder="message" /></td>
            <td v-if="showValidationError && (errorCountry.includes(country)) && (errorLanguage.includes(language))" class="message-error-icon"><i class="fa fa-exclamation-circle" aria-hidden="true"></i></td>
          </tr>
          <tr v-if="showValidationError && (errorCountry.includes(country))">
            <td class="text-red message-error-text" :colspan="4">{{ errorMessage }}</td>
          </tr>
        </table>
      </div>
      </div>
    <BlockUI v-if="loading"><i class="fa fa-cog fa-spin fa-3x fa-fw"></i><br/>Please wait...</BlockUI>
  </div>
</template>

<script>
import api from '../../../utils/api'
import { merge } from 'lodash'
import { getDisplayMessage } from '../../../utils/utilities'
export default {
  name: 'convo-message',
  props: ['message', 'index', 'countryLangMapping', 'hideEmptyTranslations', 'selectedViewOption', 'emitUpdatedMessage', 'validateMessage', 'getViewOption', 'stageName'],
  data () {
    return {
      displayMessage: getDisplayMessage(this.message, this.hideEmptyTranslations),
      messageRow: '',
      messageViewClass: 'collapse in',
      messageStyleClass: 'expand-style',
      showValidationError: false,
      currentMessageViewOption: 0,
      errorMessage: '',
      errorCountry: [],
      errorLanguage: [],
      loading: false,
      translateSwitch: {},
      lastStageName: this.stageName
    }
  },
  mounted () {
    this.refreshMessage(this.message)
  },
  methods: {
    toggleView () {
      const toggledOption = this.currentMessageViewOption === 0 ? 1 : 0
      this.currentMessageViewOption = toggledOption
      this.updateMessageViewStyle(toggledOption)
    },
    lockAll () {
      this.refreshMessage(this.displayMessage)
    },
    unlockAll () {
      this.refreshMessage(this.displayMessage, true)
    },
    async translate () {
      if (this.displayMessage && this.displayMessage['Global-eng'] && this.displayMessage['Global-eng']['eng'] && this.displayMessage['Global-eng']['eng'].trim() !== '') {
        // translate
        this.loading = true
        // make axios call
        const targetLanguages = [...new Set(Object.values(this.translateSwitch).flatMap(obj => Object.entries(obj).filter(([_, v]) => v).map(([k]) => k)))]

        if (targetLanguages.length === 0) {
          this.$swal({
            title: 'Failure',
            type: 'error',
            text: 'Please select at least one language to translate'
          })
          this.loading = false
          return
        }

        const messageToBeTranslated = [{eng: this.displayMessage['Global-eng']['eng']}]

        try {
          const response = await api.buildRequest(`getTranslation`, 'POST', {
            messageToBeTranslated,
            targetLanguages
          })

          const translatedContent = response.data.payload.translations[0]

          for (const region in this.displayMessage) {
            for (const lang in this.displayMessage[region]) {
              const switchValue = this.translateSwitch && this.translateSwitch[region] && this.translateSwitch[region][lang] && region !== 'Global-eng'
              if (switchValue === true) {
                this.displayMessage[region][lang] = translatedContent[lang] || ''
              }
            }
          }

          this.$emit('updateMessage', {index: this.index, displayMessage: this.displayMessage})
          this.loading = false
        } catch (err) {
          console.error('Error in translation', err)
          this.$swal({
            title: 'Failure',
            type: 'error',
            text: 'Error in translating message'
          })
          this.loading = false
          return
        }
      } else {
        // Show error that global message is not set
        this.$swal({
          title: 'Failure',
          type: 'error',
          text: 'Please set the global message first'
        })
      }
    },
    deleteMessage (index) {
      this.$emit('deleteMessage', index)
    },
    getCountryName: function (value) {
      if (value.split('-').length > 1) return value.split('-')[0]
      return value
    },
    async eraseTranslation (country, language) {
      const result = await this.buttonDialog('Are you sure you want to erase this whole translation?', 'Yes', 'No')
      if (!(result.dismiss && result.dismiss === 'cancel')) this.displayMessage[country][language] = ''
    },
    async buttonDialog (title, confirmButtonText, cancelButtonText) {
      return this.$swal({
        title,
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText,
        allowOutsideClick: false,
        allowEscapeKey: false
      })
    },
    refreshMessage (message, switchStatus = false) {
      this.displayMessage = getDisplayMessage(message, this.hideEmptyTranslations)
      for (const country in this.displayMessage) {
        this.translateSwitch[country] = {}
        for (const lang in this.displayMessage[country]) {
          this.translateSwitch[country][lang] = switchStatus
        }
      }
    },
    isMessageValid () {
      const message = this.displayMessage
      const mediaType = this.validateMessage.mediaType
      // Message set validation to check that video or image has correct url
      if (mediaType === 'video' || mediaType === 'image') {
        for (const country in message) {
          for (const lang in message[country]) {
            const matchpattern = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\\+.,~#?&//=]*)$/gm
            if (message[country][lang].length > 0 && !matchpattern.test(message[country][lang])) {
              this.errorMessage = `Please enter valid url for ${mediaType}`
              this.errorCountry = [...this.errorCountry, country]
              this.errorLanguage = [...this.errorLanguage, lang]
              this.showValidationError = true
            }
          }
        }
      }
      // Open a message that needs fixing; leave the others as the user left them
      if (this.showValidationError) {
        this.currentMessageViewOption = 0
        this.updateMessageViewStyle(0)
        return false
      }
      this.errorMessage = ''
      this.showValidationError = false
      return true
    },
    updateMessageViewStyle (messageStyle) {
      this.messageViewClass = messageStyle === 0 ? 'collapse in' : 'collapse'
      this.messageStyleClass = messageStyle === 0 ? 'expand-style' : 'collapse-style'
    }
  },
  watch: {
    validateMessage: function (val) {
      if (val.shouldCheckValidation) {
        this.errorMessage = ''
        this.showValidationError = false
        this.errorCountry = []
        this.errorLanguage = []
        const isValid = this.isMessageValid()
        this.$emit('updateMessageValidation', {index: this.index, isValid})
      }
    },
    emitUpdatedMessage: function (val) {
      if (val) {
        this.$emit('updateMessage', {index: this.index, displayMessage: this.displayMessage})
      }
    },
    // The message object is replaced on every save and edit check, so only refresh the text here:
    // resetting open/closed too is what kept reopening messages the user had closed.
    message: function (val) {
      this.refreshMessage(this.message)
    },
    // A different stage starts from Expand All / Collapse All again. The name blanks out for a
    // moment after a save, so an empty name isn't a new stage.
    stageName: function (val) {
      if (!val || val === this.lastStageName) return
      this.lastStageName = val
      this.currentMessageViewOption = this.getViewOption(this.index)
      this.updateMessageViewStyle(this.currentMessageViewOption)
    },
    hideEmptyTranslations: function () {
      // Before refreshing current message with message prop, update the message with whatever data is stored in this component if any
      const filledDataMessage = merge(this.message, this.displayMessage)
      this.refreshMessage(filledDataMessage)
    },
    selectedViewOption: function (val) {
      const result = this.getViewOption(this.index)
      this.currentMessageViewOption = result
      this.updateMessageViewStyle(result)
    }
  }
}
</script>
<style scoped>
.message-card {
  margin-bottom: 10px;
  border: 1px solid #E2E8F0;
  border-left: 3px solid #CFD8E3;
  border-radius: 10px;
  background: #FFFFFF;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.message-card:hover {
  box-shadow: 0 4px 16px rgba(30, 58, 95, 0.08);
}
.message-card--open {
  border-left-color: #0D9488;
}
.message-card--error {
  border-left-color: #E05263;
}
.message-card__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px 8px 12px;
  cursor: pointer;
  user-select: none;
  border-radius: 10px;
  outline: none;
}
.message-card__header:hover {
  background: #F5F7FA;
}
.message-card__header:focus-visible {
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.18);
}
.message-card__grip {
  color: #CFD8E3;
  cursor: grab;
}
.message-card__chevron {
  font-size: 11px;
  color: #8896A5;
  transform: rotate(-90deg);
  transition: transform 0.2s ease;
}
.message-card--open .message-card__chevron {
  transform: none;
}
.message-card__title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #1E3A5F;
}
.message-card__badge {
  padding: 2px 8px;
  border-radius: 999px;
  background: #FCE8EB;
  color: #C94455;
  font-size: 11px;
  font-weight: 700;
}
.message-card__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  cursor: default;
}
.message-action {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid #E2E8F0;
  border-radius: 7px;
  background: #FFFFFF;
  color: #1E3A5F;
  font-size: 12px;
  font-weight: 600;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.message-action:hover {
  background: #F5F7FA;
  border-color: #CFD8E3;
}
.message-action--primary {
  background: #0D9488;
  border-color: #0D9488;
  color: #FFFFFF;
}
.message-action--primary:hover {
  background: #0B7C72;
  border-color: #0B7C72;
}
.message-action--icon {
  width: 28px;
  padding: 0;
  justify-content: center;
}
.message-action--danger {
  color: #8896A5;
}
.message-action--danger:hover {
  background: #FCE8EB;
  border-color: #FCE8EB;
  color: #C94455;
}
.fixed-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0 6px;
}
.message-country-group + .message-country-group {
  border-top: 1px dashed #E2E8F0;
}
.message-country {
  width: 96px;
  padding: 0 8px;
  vertical-align: top;
}
.message-country__name {
  display: inline-block;
  margin-top: 8px;
  padding: 3px 8px;
  border-radius: 6px;
  background: #EBF0F7;
  color: #1E3A5F;
  font-size: 12px;
  font-weight: 700;
  word-break: break-word;
}
.message-language {
  width: 72px;
  padding: 6px 4px 0;
  vertical-align: top;
  text-align: center;
}
.message-language__code {
  display: block;
  margin-bottom: 4px;
  color: #5C6E8A;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
.clear-translation {
  width: 26px;
  padding: 8px 2px 0;
  vertical-align: top;
}
.message-erase {
  padding: 0;
  border: none;
  background: none;
  color: #CFD8E3;
  font-size: 15px;
  transition: color 0.15s ease;
}
.message-erase:hover {
  color: #E05263;
}
.message-text {
  border-radius: 8px;
  resize: vertical;
}
.message-error-icon {
  width: 24px;
  padding-top: 8px;
  color: #E05263;
  text-align: center;
  vertical-align: top;
}
.message-error-text {
  padding: 0 8px;
  font-size: 12px;
}
.collapse-style,
.expand-style {
  width: 100%;
  padding: 0 10px 6px;
}
.collapse.in.expand-style {
  animation: message-reveal 0.2s ease-out;
}
@keyframes message-reveal {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: none; }
}
</style>



// WEBPACK FOOTER //
// convoMessage.vue?6038d63a